import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { Alert, Box, Button, CircularProgress, Stack, Typography } from "@mui/material";

import {
  formatProductionQuantity,
  formatProductionYield,
} from "../utils/productionFormatters";
import { PRODUCTION_COMPONENT_TYPE_LABELS } from "../utils/productionModel";

const columns = [
  { key: "component", label: "Componente", width: "minmax(150px, 1.6fr)" },
  { key: "type", label: "Tipo", width: "minmax(90px, .8fr)" },
  { key: "net", label: "Neto de composición", width: "minmax(130px, 1.1fr)" },
  { key: "yield", label: "Rendimiento", width: "minmax(100px, .85fr)" },
  { key: "required", label: "Necesario", width: "minmax(110px, .95fr)" },
  { key: "stock", label: "Stock actual", width: "minmax(110px, .95fr)" },
  { key: "status", label: "Estado", width: "minmax(105px, .9fr)" },
];

const resultGridSx = {
  display: "grid",
  gridTemplateColumns: columns.map((column) => column.width).join(" "),
  gap: 0.75,
  alignItems: "center",
  "& > *": { minWidth: 0 },
};

function TextCell({ children, title }) {
  return (
    <Typography
      noWrap
      title={title || (typeof children === "string" ? children : undefined)}
      sx={{
        color: "primary.900",
        fontSize: 12,
        fontWeight: 650,
        textAlign: "center",
      }}
    >
      {children}
    </Typography>
  );
}

function Availability({ line }) {
  if (!line.stockControlled) {
    return (
      <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", justifyContent: "center" }}>
        <RemoveCircleOutlineIcon sx={{ color: "neutral.600", fontSize: 17 }} />
        <Typography sx={{ color: "neutral.700", fontSize: 11, fontWeight: 800 }}>
          Sin control
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", justifyContent: "center" }}>
      {line.isAvailable ? (
        <CheckCircleOutlineIcon sx={{ color: "success.dark", fontSize: 18 }} />
      ) : (
        <HighlightOffIcon sx={{ color: "error.main", fontSize: 18 }} />
      )}
      <Typography
        sx={{
          color: line.isAvailable ? "success.dark" : "error.dark",
          fontSize: 11,
          fontWeight: 800,
        }}
      >
        {line.isAvailable ? "Suficiente" : "Faltante"}
      </Typography>
    </Stack>
  );
}

function resolveMissingUnit(missing, lines) {
  return (
    lines.find(
      (line) =>
        String(line.componentType) === String(missing.componentType) &&
        Number(line.componentId) === Number(missing.componentId)
    )?.unit || ""
  );
}

export default function ProductionCalculationResult({
  calculation,
  confirming = false,
  confirmationError = "",
  onProduce,
}) {
  if (!calculation) {
    return (
      <Stack
        spacing={1.5}
        sx={{ flex: 1, alignItems: "center", justifyContent: "center", textAlign: "center" }}
      >
        <Typography
          component="h2"
          sx={{ color: "primary.900", fontSize: 18, fontWeight: 750 }}
        >
          Resultado del cálculo
        </Typography>
        <Typography sx={{ color: "neutral.600", fontSize: 14, fontWeight: 650 }}>
          Completá el formulario y calculá la producción para revisar su
          disponibilidad.
        </Typography>
      </Stack>
    );
  }

  const lines = Array.isArray(calculation.lines) ? calculation.lines : [];
  const missing = Array.isArray(calculation.missing) ? calculation.missing : [];
  const issues = Array.isArray(calculation.issues) ? calculation.issues : [];

  return (
    <Stack spacing={1.7} sx={{ width: "100%", minWidth: 0 }}>
      <Box>
        <Typography
          component="h2"
          sx={{ color: "primary.900", fontSize: 18, fontWeight: 800 }}
        >
          {calculation.preparedProduct?.name || "Elaborado"}
        </Typography>
        <Typography sx={{ mt: 0.35, color: "neutral.700", fontSize: 14, fontWeight: 700 }}>
          Cantidad calculada: {formatProductionQuantity(
            calculation.producedQuantity,
            calculation.preparedProduct?.resultUnit
          )}
        </Typography>
      </Box>

      <Box sx={{ borderRadius: "18px", overflow: "hidden", bgcolor: "neutral.50" }}>
        <Box sx={{ overflowX: "auto" }}>
          <Box sx={{ minWidth: 850 }}>
            <Box
              sx={{
                ...resultGridSx,
                px: 1.5,
                py: 1.15,
                bgcolor: "neutral.600",
              }}
            >
              {columns.map((column) => (
                <Typography
                  key={column.key}
                  sx={{
                    color: "logoblanco",
                    fontSize: 12,
                    fontWeight: 750,
                    lineHeight: 1.15,
                    textAlign: "center",
                  }}
                >
                  {column.label}
                </Typography>
              ))}
            </Box>

            {lines.map((line, index) => (
              <Box
                key={`${line.componentType}-${line.componentId}-${line.lineOrder || index}`}
                sx={{
                  ...resultGridSx,
                  px: 1.5,
                  py: 1.15,
                  borderBottom: "1px solid rgba(0,0,0,.06)",
                  "&:last-of-type": { borderBottom: 0 },
                }}
              >
                <TextCell title={line.name}>{line.name || "—"}</TextCell>
                <TextCell>
                  {PRODUCTION_COMPONENT_TYPE_LABELS[line.componentType] ||
                    line.componentType ||
                    "—"}
                </TextCell>
                <TextCell>
                  {formatProductionQuantity(line.netQuantity, line.unit)}
                </TextCell>
                <TextCell>{formatProductionYield(line.yieldFactor)}</TextCell>
                <TextCell>
                  {formatProductionQuantity(line.requiredQuantity, line.unit)}
                </TextCell>
                <TextCell>
                  {line.stockControlled
                    ? formatProductionQuantity(line.currentStock, line.unit)
                    : "Sin control"}
                </TextCell>
                <Availability line={line} />
              </Box>
            ))}

            {!lines.length ? (
              <Typography sx={{ p: 2, color: "neutral.700", fontSize: 13, fontWeight: 650 }}>
                El cálculo no devolvió componentes directos.
              </Typography>
            ) : null}
          </Box>
        </Box>
      </Box>

      <Box
        role="status"
        sx={{
          px: 1.5,
          py: 1,
          borderRadius: "14px",
          bgcolor: calculation.canProduce
            ? "rgba(33, 150, 83, 0.14)"
            : "rgba(195, 60, 60, 0.14)",
        }}
      >
        <Typography
          sx={{
            color: calculation.canProduce ? "success.dark" : "error.dark",
            fontSize: 16,
            fontWeight: 850,
          }}
        >
          {calculation.canProduce ? "SE PUEDE PRODUCIR" : "NO SE PUEDE PRODUCIR"}
        </Typography>
      </Box>

      {missing.length ? (
        <Stack spacing={0.45}>
          <Typography sx={{ color: "primary.900", fontSize: 14, fontWeight: 800 }}>
            Faltantes:
          </Typography>
          {missing.map((item, index) => (
            <Typography
              key={`${item.componentType}-${item.componentId}-${index}`}
              sx={{ color: "error.dark", fontSize: 13, fontWeight: 700 }}
            >
              {item.name || "Componente"}: {formatProductionQuantity(
                item.missingQuantity,
                resolveMissingUnit(item, lines)
              )}
            </Typography>
          ))}
        </Stack>
      ) : null}

      {issues.length ? (
        <Alert severity="error" sx={{ borderRadius: "14px", fontSize: 12, fontWeight: 650 }}>
          <Typography component="span" sx={{ display: "block", fontSize: 13, fontWeight: 800 }}>
            Problemas de costo o composición:
          </Typography>
          {issues.map((issue, index) => (
            <Typography
              component="span"
              key={`${issue.code || "issue"}-${issue.componentId || index}`}
              sx={{ display: "block", mt: 0.35, fontSize: 12, fontWeight: 650 }}
            >
              {issue.message || "No se pudo validar el componente."}
            </Typography>
          ))}
        </Alert>
      ) : null}

      {confirmationError ? (
        <Alert severity="error" sx={{ borderRadius: "14px", fontSize: 12, fontWeight: 650 }}>
          {confirmationError}
        </Alert>
      ) : null}

      <Button
        type="button"
        disabled={!calculation.canProduce || confirming}
        onClick={onProduce}
        startIcon={confirming ? <CircularProgress size={15} color="inherit" /> : null}
        sx={{
          alignSelf: "flex-end",
          minWidth: 150,
          height: 38,
          px: 2.4,
          borderRadius: "33px",
          bgcolor: "primary.500",
          color: "logoblanco",
          textTransform: "none",
          fontSize: 12,
          fontWeight: 800,
          boxShadow: "0px 4px 12px rgba(23, 31, 76, 0.18)",
          "&:hover": { bgcolor: "primary.600" },
          "&.Mui-disabled": {
            bgcolor: "secondary.100",
            color: "neutral.500",
          },
        }}
      >
        {confirming ? "Produciendo..." : "Producir"}
      </Button>
    </Stack>
  );
}
